import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarPlus, Pencil } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getJSON, patchJSON } from '@/lib/api'
import { OrderCancellation } from '@/components/events/order-cancellation'
import { OrderReduction } from '@/components/events/order-reduction'
import { AttendeeQrCode } from '@/components/events/attendee-qr-code'
import { InvoiceStatus } from '@/components/events/invoice-status'
import { OwnedPortalEventPage } from '@/components/member-portal/OwnedPortalEventPage'
import type { Association } from '@/pages/StaffSignInPage'

export interface OrderLink {
  status: string
  cancelled: boolean
  editable: boolean
  cancellable: boolean
  reducible: boolean
  cancellation_note: string | null
  reduction_note: string | null
  ordered_date: string
  event: {
    name: string
    schedule: string
    time_zone_label: string
    location: string | null
    calendar_url: string
    source_url?: string | null
    hero_image: { url: string; filename: string } | null
    cancelled: boolean
  }
  tickets: Array<{ name: string; quantity: number; price_cents: number }>
  attendees: Array<{
    seat: number
    name: string | null
    email: string | null
    check_in_token: string
    checked_in?: boolean
  }>
  invoice: null | {
    number: number
    status: string
    balance: string
    due_on: string
    payment_link_url: string | null
  }
}

export interface OrderLinkResponse {
  order_link: OrderLink
}

interface AttendeeResponse {
  attendee: OrderLink['attendees'][number]
}

export function OrderLinkPage({ association, token }: { association: Association; token: string }) {
  const orderQuery = useQuery({
    queryKey: ['order-link', token],
    queryFn: () => getJSON<OrderLinkResponse>(`/api/v1/order_links/${token}`),
    retry: false,
  })

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-5xl items-center px-6 text-body-sm font-medium">
          {association.name}
        </div>
      </header>
      <div className="mx-auto w-full max-w-2xl px-6 py-8">
        {orderQuery.isLoading ? (
          <p className="text-body-sm text-muted-foreground" role="status">
            Loading order…
          </p>
        ) : orderQuery.isError || !orderQuery.data ? (
          <Alert>Order unavailable.</Alert>
        ) : (
          <EventOrderContent
            order={orderQuery.data.order_link}
            queryKey={['order-link', token]}
            attendeePath={(seat) => `/api/v1/order_links/${token}/attendees/${seat}`}
            cancelPath={`/api/v1/order_links/${token}/cancel`}
            reducePath={`/api/v1/order_links/${token}/reduce`}
          />
        )}
      </div>
    </main>
  )
}

export function EventOrderContent({
  order,
  queryKey,
  attendeePath,
  cancelPath,
  reducePath,
}: {
  order: OrderLink
  queryKey: readonly unknown[]
  attendeePath: (seat: number) => string
  cancelPath?: string
  reducePath?: string
}) {
  const [activeSeat, setActiveSeat] = React.useState<number>()

  if (order.event.source_url) {
    return <OwnedPortalEventPage event={{ ...order.event, sourceUrl: order.event.source_url }} />
  }

  return (
    <div className="space-y-4">
      {order.cancelled && <Alert>This order was cancelled.</Alert>}
      {order.event.cancelled && <Alert>This event was cancelled.</Alert>}
      <OrderCancellation order={order} queryKey={queryKey} cancelPath={cancelPath} />
      <OrderReduction order={order} queryKey={queryKey} reducePath={reducePath} />
      <p className="text-caption text-muted-foreground">Placed {order.ordered_date}</p>

      <Card>
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
          {order.event.hero_image && (
            <img
              src={order.event.hero_image.url}
              alt={order.event.name}
              className="h-14 w-14 shrink-0 rounded object-cover"
            />
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-body font-semibold">{order.event.name}</h1>
            <p className="mt-1 text-caption text-muted-foreground">
              {order.event.schedule} {order.event.time_zone_label}
            </p>
            {order.event.location && (
              <p className="mt-0.5 text-caption text-muted-foreground">{order.event.location}</p>
            )}
          </div>
          {!order.event.cancelled && (
            <Button asChild variant="outline" className="h-8 shrink-0 gap-1.5 px-3">
              <a href={order.event.calendar_url} download>
                <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
                Add to calendar
              </a>
            </Button>
          )}
        </CardContent>
      </Card>

      <InvoiceStatus invoice={order.invoice} />

      <Card className="overflow-hidden">
        <CardHeader className="border-b border-border px-4 py-3">
          <CardTitle className="text-body-sm">Attendees</CardTitle>
          {order.tickets.map((ticket) => (
            <p key={ticket.name} className="text-caption text-muted-foreground">
              {ticket.name} · {ticket.quantity} {ticket.quantity === 1 ? 'ticket' : 'tickets'}
            </p>
          ))}
        </CardHeader>
        <CardContent className="p-0">
          {order.attendees.map((attendee) => (
            <AttendeeRow
              key={attendee.seat}
              attendee={attendee}
              ticket={order.tickets[0].name}
              queryKey={queryKey}
              attendeePath={attendeePath}
              editable={order.editable}
              editing={activeSeat === attendee.seat}
              onEdit={() => setActiveSeat(attendee.seat)}
              onClose={() => setActiveSeat(undefined)}
            />
          ))}
        </CardContent>
      </Card>
      {order.editable && (
        <p className="text-caption text-muted-foreground">
          Changes can be made until the event ends.
        </p>
      )}
      {!order.cancelled &&
        !order.cancellable &&
        !order.event.cancelled &&
        order.cancellation_note && (
          <p className="text-caption text-muted-foreground">{order.cancellation_note}</p>
        )}
    </div>
  )
}

function AttendeeRow({
  attendee,
  ticket,
  queryKey,
  attendeePath,
  editable,
  editing,
  onEdit,
  onClose,
}: {
  attendee: OrderLink['attendees'][number]
  ticket: string
  queryKey: readonly unknown[]
  attendeePath: (seat: number) => string
  editable: boolean
  editing: boolean
  onEdit: () => void
  onClose: () => void
}) {
  return (
    <div className="border-b border-border px-4 py-3 last:border-b-0">
      <p className="text-caption text-muted-foreground">
        {ticket} — seat {attendee.seat}
      </p>
      {editing ? (
        <AttendeeEditor
          attendee={attendee}
          queryKey={queryKey}
          attendeePath={attendeePath}
          onClose={onClose}
        />
      ) : attendee.name ? (
        <div className="mt-1 flex items-start justify-between gap-3">
          <div className="min-w-0 sm:flex sm:items-baseline sm:gap-2">
            <p className="text-body-sm font-medium">{attendee.name}</p>
            {attendee.email && (
              <p className="text-caption text-muted-foreground">{attendee.email}</p>
            )}
          </div>
          {editable && (
            <Button
              type="button"
              variant="ghost"
              className="h-6 w-6 shrink-0 p-0 text-muted-foreground"
              aria-label={`Edit attendee ${attendee.seat}`}
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
        </div>
      ) : editable ? (
        <Button
          type="button"
          variant="outline"
          className="mt-2 h-8 border-dashed px-3 text-body-sm text-muted-foreground"
          onClick={onEdit}
        >
          + Add name
        </Button>
      ) : (
        <p className="mt-1 text-caption text-muted-foreground">Name not provided</p>
      )}
      <AttendeeQrCode attendee={attendee} />
    </div>
  )
}

function AttendeeEditor({
  attendee,
  queryKey,
  attendeePath,
  onClose,
}: {
  attendee: OrderLink['attendees'][number]
  queryKey: readonly unknown[]
  attendeePath: (seat: number) => string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = React.useState(attendee.name ?? '')
  const [email, setEmail] = React.useState(attendee.email ?? '')
  const update = useMutation({
    mutationFn: () =>
      patchJSON<AttendeeResponse>(attendeePath(attendee.seat), {
        attendee: { name, email },
      }),
    onSuccess: ({ attendee: savedAttendee }) => {
      queryClient.setQueryData<OrderLinkResponse>(queryKey, (current) =>
        current
          ? {
              order_link: {
                ...current.order_link,
                attendees: current.order_link.attendees.map((item) =>
                  item.seat === savedAttendee.seat ? savedAttendee : item,
                ),
              },
            }
          : current,
      )
      onClose()
    },
  })

  return (
    <form
      noValidate
      className="mt-2 grid gap-2 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault()
        update.mutate()
      }}
    >
      <div className="space-y-1">
        <Label
          htmlFor={`attendee-${attendee.seat}-name`}
          className="text-caption text-muted-foreground"
        >
          Name
        </Label>
        <Input
          id={`attendee-${attendee.seat}-name`}
          value={name}
          className="h-8 bg-background"
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label
          htmlFor={`attendee-${attendee.seat}-email`}
          className="text-caption text-muted-foreground"
        >
          Email
        </Label>
        <Input
          id={`attendee-${attendee.seat}-email`}
          type="email"
          value={email}
          className="h-8 bg-background"
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      {update.error && (
        <p className="text-caption text-destructive sm:col-span-2" role="alert">
          {update.error.message}
        </p>
      )}
      <div className="flex items-center gap-2 sm:col-span-2">
        <Button type="submit" variant="outline" className="h-8 px-3" disabled={update.isPending}>
          Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-8 px-2 text-muted-foreground"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
